import { Package, Plus, XCircle } from 'lucide-react';
import { LazyImage, getProductThumbnailUrl } from '@paulownia/shared';
import { isProductOutOfStock } from '@paulownia/shared/types';
import type { ProductRowCardProps } from './types';
import { PriceDisplay } from './PriceDisplay';

function ProductImagePlaceholder() {
    return (
        <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Package className="w-8 h-8 text-gray-400" />
        </div>
    );
}

export function ProductRowCard({ product, onAddClick }: ProductRowCardProps) {
    // Use thumbnail if available, fallback to full image or legacy picture URL
    const imageUrl = getProductThumbnailUrl(product);
    const hasImage = !!imageUrl;
    const outOfStock = isProductOutOfStock(product);

    const handleClick = () => {
        if (!outOfStock) {
            onAddClick(product);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={outOfStock}
            className={`relative w-full text-left bg-white rounded-lg border border-gray-200 p-4 flex gap-4 items-start transition-all ${
                outOfStock
                    ? 'opacity-60 grayscale cursor-not-allowed'
                    : 'hover:shadow-md active:scale-[0.98] cursor-pointer'
            }`}
        >
            {/* Add icon badge - top right */}
            {!outOfStock && (
                <div className="absolute -top-2 -right-2 w-7 h-7 bg-orange-600 rounded-full flex items-center justify-center shadow-md">
                    <Plus className="w-4 h-4 text-white" />
                </div>
            )}

            {/* Image or placeholder */}
            <div className="flex-shrink-0">
                {hasImage ? (
                    <LazyImage
                        src={imageUrl!}
                        alt={product.name}
                        width={80}
                        height={80}
                        className="w-20 h-20 object-cover rounded-lg"
                        fallback={<ProductImagePlaceholder />}
                    />
                ) : (
                    <ProductImagePlaceholder />
                )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 flex gap-2">
                {/* Left: Title, description, disclaimer */}
                <div className="flex-1 min-w-0">
                    <h3 className={`font-bold text-base leading-tight ${outOfStock ? 'text-gray-500' : 'text-gray-900'}`}>
                        {product.name}
                    </h3>
                    {product.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">{product.description}</p>
                    )}
                    {/* Disclaimer for estimated prices */}
                    {product.disclaimer && !outOfStock && (
                        <p className="text-xs text-orange-600 mt-1 italic">{product.disclaimer}</p>
                    )}
                    {outOfStock && (
                        <div className="flex items-center gap-2 text-red-600 mt-2">
                            <XCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Indisponible aujourd'hui</span>
                        </div>
                    )}
                </div>
                {/* Right: Price */}
                <div className={`flex-shrink-0 ${outOfStock ? 'opacity-50' : ''}`}>
                    <PriceDisplay product={product} compact />
                </div>
            </div>
        </button>
    );
}
